<template>
  <div class="side-navigation-menu" @click="forwardClick">
    <slot />
    <div class="menu-container">
      <dx-tree-view
        :ref="treeViewRef"
        :items="items"
        key-expr="path"
        width="100%"
        expand-event="click"
        selection-mode="none"
        :select-by-click="false"
        :focus-state-enabled="false"
        :select-nodes-recursive="true"
        @initialized="onInitialized"
        @item-click="handleItemClick"
      >
        <template #item="item">
          <div
            class="dx-item-content dx-treeview-item-content"
            :class="{'menu-selected': isActive(item.data)}"
          >
            <font-awesome-icon class="dx-icon" :icon="item.data.icon" />
            <span style="font-weight:400;">{{ item.data.text }}</span>
          </div>
        </template>
      </dx-tree-view>
    </div>
  </div>
</template>

<script>
import DxTreeView from "devextreme-vue/ui/tree-view";
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faShoppingCart, faTools, faRubleSign, faStream, faExchangeAlt, faUsers, faListOl, faUserSecret, faProjectDiagram,
  faBookOpen, faLayerGroup, faBell, faEnvelope, faCertificate, faChartPie, faBuilding, faCube, faEye, faPen, faWrench,
  faInfo,
} from '@fortawesome/free-solid-svg-icons';
import { faScanner } from './icons/faScanner';
import { faGear } from './icons/faGear';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faShoppingCart, faTools, faRubleSign, faStream, faExchangeAlt, faUsers, faListOl, faUserSecret,
faProjectDiagram, faBookOpen, faScanner, faLayerGroup, faBell, faGear, faEnvelope, faCertificate, faChartPie,
faBuilding, faCube, faEye, faPen, faWrench, faInfo,
);

const treeViewRef = "treeViewRef";

export default {
  props: {
    items: Array,
    selectedItem: String,
    compactMode: Boolean
  },
  components: {
    DxTreeView, FontAwesomeIcon
  },
  data() {
    return {
      treeViewRef
    };
  },
  computed: {
    activeMenu() {
      return this.$route.meta && this.$route.meta.menu;
    }
  },
  methods: {
    forwardClick(...args) {
      this.$emit("click", args);
    },
    isActive(item) {
      return item.name === this.activeMenu;
    },
    onInitialized(e) {
      this.treeView = e.component;

      this.$nextTick(() => {
        this.syncSelection();
      });
    },
    handleItemClick(e) {
      const path = e.itemData.path;

      if (!path || this.compactMode) return;

      if (this.$route.path === path) return;

      this.$router.push(path).catch(() => {});
    },
    syncSelection() {
      if (!this.treeView) return;

      const key = this.$route.meta && this.$route.meta.menu;
      if (!key) return;

      const selected = this.treeView.getSelectedNodeKeys();

      if (selected && selected.includes(key)) return;

      this.treeView.selectItem(key);
    }
  },
  mounted() {
    this.treeView = this.$refs[treeViewRef] && this.$refs[treeViewRef].instance;

    if (this.compactMode) {
      this.treeView.collapseAll();
    }
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler() {
        this.syncSelection();
      }
    },
    compactMode() {
      if (this.compactMode) {
        this.treeView.collapseAll();
      }
    }
  }
};
</script>

<style lang="scss">
@import "../dx-styles.scss";
@import "../themes/generated/variables.printstat.scss";

.side-navigation-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  height: 100%;
  width: 220px !important;
  background-color: $base-bg;

  .menu-container {
    min-height: 100%;
    display: flex;
    flex: 1;

    .dx-treeview {
      // ## Long text positioning
      white-space: nowrap;
      // ##

      // ## Icon width customization
      .dx-treeview-item {
        padding: 0;
        margin: 0;

        .dx-icon {
          width: $side-panel-min-width !important;
          margin: 0 !important;
        }
        .dx-item-content {
          padding-top: 10px;
          padding-bottom: 10px;
        }
      }
      // ##

      // ## Arrow customization
      .dx-treeview-node {
        padding: 0 0 !important;
      }

      .dx-treeview-toggle-item-visibility {
        right: 10px;
        left: auto;
        top: 5px;
      }

      .dx-rtl .dx-treeview-toggle-item-visibility {
        left: 10px;
        right: auto;
      }
      // ##

      // ## Item levels customization
      .dx-treeview-node {
        &[aria-level="1"] {
          font-weight: bold;
        }

        &[aria-level="2"] .dx-treeview-item-content {
          font-weight: normal;
          padding-left: 39px;
        }
      }
      // ##
    }

    .menu-selected {
      background-color: #337ab7;
      border-bottom: 1px solid #337ab7;
      color: #fff;
    }
  }
}
</style>
